import type { CloudWorkspace } from './api-client';

const WORKSPACE_CACHE_PREFIX = 'budget.cloud.workspaces.v1:';

type StorageLike = Pick<Storage, 'getItem' | 'setItem'>;

export type WorkspaceCache = Readonly<{
  items: readonly CloudWorkspace[];
  selectedWorkspaceId: string | null;
  updatedAt: number;
}>;

export function loadWorkspaceCache(
  subject: string,
  storage: StorageLike = localStorage,
): WorkspaceCache | null {
  const raw = storage.getItem(cacheKey(subject));
  if (raw === null) return null;
  try {
    const value: unknown = JSON.parse(raw);
    if (
      !isRecord(value) ||
      !Array.isArray(value.items) ||
      !value.items.every(isCloudWorkspace) ||
      !isNullableString(value.selectedWorkspaceId) ||
      typeof value.updatedAt !== 'number' ||
      !Number.isFinite(value.updatedAt)
    ) {
      return null;
    }
    const selectedWorkspaceId = selectAvailableWorkspace(
      value.items,
      value.selectedWorkspaceId,
    );
    return {
      items: value.items,
      selectedWorkspaceId,
      updatedAt: value.updatedAt,
    };
  } catch {
    return null;
  }
}

export function saveWorkspaceCache(
  subject: string,
  items: readonly CloudWorkspace[],
  selectedWorkspaceId: string | null,
  storage: StorageLike = localStorage,
  now: number = Date.now(),
): WorkspaceCache {
  const cache: WorkspaceCache = {
    items,
    selectedWorkspaceId: selectAvailableWorkspace(items, selectedWorkspaceId),
    updatedAt: now,
  };
  storage.setItem(cacheKey(subject), JSON.stringify(cache));
  return cache;
}

export function selectAvailableWorkspace(
  items: readonly CloudWorkspace[],
  selectedWorkspaceId: string | null,
): string | null {
  if (
    selectedWorkspaceId !== null &&
    items.some(workspace => workspace.id === selectedWorkspaceId)
  ) {
    return selectedWorkspaceId;
  }
  return items[0]?.id ?? null;
}

function cacheKey(subject: string): string {
  return `${WORKSPACE_CACHE_PREFIX}${encodeURIComponent(subject)}`;
}

function isCloudWorkspace(value: unknown): value is CloudWorkspace {
  return (
    isRecord(value) &&
    typeof value.id === 'string' &&
    typeof value.name === 'string' &&
    typeof value.created_at === 'string' &&
    typeof value.created_by_subject === 'string'
  );
}

function isNullableString(value: unknown): value is string | null {
  return value === null || typeof value === 'string';
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}
